import React, { useState, useMemo } from "react";
import {
  FaMicrochip,
  FaBolt,
  FaSearch,
  FaTimes,
  FaClock,
} from "react-icons/fa";
import { formatDisplayName } from "../../../utils/textUtils";
import { getSensorStatus, formatResistance } from "../../../utils/sensorStatus";
import { useQuery } from "@tanstack/react-query";
import getApiServices from "../../../services/getApiService";

const glassPanel = {
  background: "rgba(13,21,38,0.78)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow:
    "0 12px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
};

interface SiteSensorCardProps {
  sensor: any;
  criticalThreshold?: number;
}

const statusBadgeStyles: Record<
  string,
  { bg: string; border: string; color: string; dot: string; label: string }
> = {
  Active: {
    bg: "rgba(16,185,129,0.12)",
    border: "rgba(16,185,129,0.25)",
    color: "#34d399",
    dot: "#10b981",
    label: "Active",
  },
  Critical: {
    bg: "rgba(239,68,68,0.12)",
    border: "rgba(239,68,68,0.28)",
    color: "#f87171",
    dot: "#ef4444",
    label: "Critical",
  },
  Inactive: {
    bg: "rgba(100,116,139,0.12)",
    border: "rgba(100,116,139,0.20)",
    color: "#94a3b8",
    dot: "#64748b",
    label: "Inactive",
  },
};

const SiteSensorCard: React.FC<SiteSensorCardProps> = ({
  sensor,
  criticalThreshold = 3.0,
}) => {
  const { data: latestData } = useQuery({
    queryKey: ["site_sensor_data", sensor.name],
    queryFn: () =>
      getApiServices(`/be-service/sensors/latestData?deviceId=${sensor.name}`),
    enabled: !!sensor.name,
    refetchInterval: 30000,
  });

  const rVal = latestData?.R ?? latestData?.r ?? latestData?.resistance ?? null;
  const gVal = latestData?.G ?? latestData?.g ?? latestData?.ground ?? null;
  const lastUpdatedRaw = latestData?.timestamp || sensor.lastUpdatedAt;

  const lastUpdated = lastUpdatedRaw ? new Date(lastUpdatedRaw) : null;
  const now = new Date();
  let minutesAgo: number | null = null;
  if (lastUpdated && !isNaN(lastUpdated.getTime())) {
    minutesAgo = Math.max(
      0,
      Math.round((now.getTime() - lastUpdated.getTime()) / 60000)
    );
  }

  const status = getSensorStatus(rVal, lastUpdatedRaw, gVal, criticalThreshold);
  const badgeStyle = statusBadgeStyles[status] || statusBadgeStyles["Inactive"];

  return (
    <li
      className="group p-3.5 sm:p-4 rounded-2xl transition-all duration-200 border relative overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.02)",
        borderColor:
          status === "Critical"
            ? "rgba(239,68,68,0.25)"
            : "rgba(255,255,255,0.05)",
        boxShadow:
          status === "Critical"
            ? "0 4px 16px rgba(239,68,68,0.10)"
            : "0 2px 10px rgba(0,0,0,0.2)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* Earth pit name */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-white truncate group-hover:text-blue-300 transition-colors">
              {formatDisplayName(sensor.name)}
            </span>
          </div>

          {/* Description */}
          {sensor.description && (
            <p className="text-xs text-slate-400 truncate mt-0.5 max-w-xs">
              {sensor.description}
            </p>
          )}

          {/* Metrics row */}
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            {/* Sensor External ID */}
            {sensor.sensorExternalId && (
              <span className="text-[10px] bg-slate-800/80 text-slate-400 px-2 py-0.5 rounded-md font-mono border border-slate-700/60">
                #{sensor.sensorExternalId}
              </span>
            )}

            {/* Resistance reading */}
            {rVal != null ? (
              <div
                className={`text-[11px] px-2.5 py-0.5 rounded-lg font-mono font-bold flex items-center gap-1 border transition-all ${
                  status === "Critical"
                    ? "bg-red-500/15 text-red-300 border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.2)]"
                    : "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
                }`}
                title={`Live resistance: ${formatResistance(rVal)} (Threshold: ${criticalThreshold} Ω)`}
              >
                <FaBolt
                  className={`w-2.5 h-2.5 ${
                    status === "Critical" ? "text-red-400 animate-pulse" : "text-emerald-400"
                  }`}
                />
                <span>{formatResistance(rVal)}</span>
              </div>
            ) : (
              <span className="text-[11px] text-slate-500 font-mono px-2 py-0.5 rounded bg-white/[0.02]">
                No reading
              </span>
            )}

            {/* Relative timestamp */}
            <div className="text-[11px] text-slate-400 flex items-center gap-1">
              <FaClock className="w-2.5 h-2.5 opacity-60" />
              <span>
                {minutesAgo !== null
                  ? minutesAgo === 0
                    ? "Just now"
                    : `${minutesAgo}m ago`
                  : "No data"}
              </span>
            </div>
          </div>
        </div>

        {/* Status Pill Badge */}
        <div className="flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm"
            style={{
              background: badgeStyle.bg,
              borderColor: badgeStyle.border,
              color: badgeStyle.color,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: badgeStyle.dot,
                boxShadow: `0 0 6px ${badgeStyle.dot}`,
              }}
            />
            <span>{badgeStyle.label}</span>
          </div>
        </div>
      </div>
    </li>
  );
};

interface SensorListProps {
  selectedSystem: any;
  criticalThreshold?: number;
}

const SensorList: React.FC<SensorListProps> = ({
  selectedSystem,
  criticalThreshold,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const sensors = selectedSystem?.sensors || [];

  const filteredSensors = useMemo(() => {
    if (!searchQuery.trim()) return sensors;
    const q = searchQuery.toLowerCase().trim();
    return sensors.filter((s: any) => {
      const name = (s.name || "").toLowerCase();
      const desc = (s.description || "").toLowerCase();
      const extId = String(s.sensorExternalId || "");
      return name.includes(q) || desc.includes(q) || extId.includes(q);
    });
  }, [sensors, searchQuery]);

  return (
    <aside
      className="w-full xl:w-84 2xl:w-96 flex-shrink-0 flex flex-col rounded-2xl overflow-hidden min-h-[380px] xl:min-h-0 xl:h-full z-10 transition-all duration-300"
      style={glassPanel}
    >
      {/* Header */}
      <div
        className="px-4 sm:px-5 py-3.5 sm:py-4 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(59,130,246,0.2) 0%, rgba(14,165,233,0.15) 100%)",
                border: "1px solid rgba(59,130,246,0.3)",
                boxShadow: "0 0 16px rgba(59,130,246,0.2)",
              }}
            >
              <FaMicrochip className="w-4 h-4 text-blue-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-bold text-white tracking-tight truncate">
                Connected Earth Pits
              </h3>
              {selectedSystem && (
                <p className="text-[10px] text-blue-300 font-semibold truncate">
                  {formatDisplayName(selectedSystem.name)}
                </p>
              )}
            </div>
          </div>

          {criticalThreshold != null && (
            <span
              className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full border flex-shrink-0"
              style={{
                background: "rgba(245, 158, 11, 0.12)",
                borderColor: "rgba(245, 158, 11, 0.3)",
                color: "#fcd34d",
              }}
              title="Site Critical Resistance Threshold"
            >
              Limit: {Number(criticalThreshold).toFixed(1)} Ω
            </span>
          )}
        </div>
      </div>

      {/* Filter box if > 3 sensors */}
      {sensors.length > 3 && (
        <div
          className="px-3 sm:px-4 py-2.5 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          <div className="relative flex items-center">
            <FaSearch className="w-3.5 h-3.5 text-slate-400 absolute left-3 pointer-events-none" />
            <input
              type="text"
              placeholder="Search earth pits…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-8 py-1.5 text-xs rounded-xl text-white placeholder-slate-500 transition-all outline-none"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "rgba(59,130,246,0.5)";
                e.target.style.boxShadow = "0 0 0 2px rgba(59,130,246,0.15)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "rgba(255,255,255,0.08)";
                e.target.style.boxShadow = "none";
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 p-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <FaTimes className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Sensor List Area */}
      <div className="p-3 sm:p-3.5 flex-1 overflow-y-auto custom-scrollbar min-h-0">
        {!selectedSystem ? (
          <div className="py-20 flex flex-col items-center justify-center text-slate-500 text-center px-4 gap-3">
            <div
              className="p-3.5 rounded-2xl"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <FaMicrochip className="w-8 h-8 opacity-30 text-blue-400" />
            </div>
            <p className="text-xs sm:text-sm font-semibold text-slate-300">
              Select a System
            </p>
            <p className="text-xs text-slate-500 max-w-[200px]">
              Choose a distribution system to view real-time earth pit readings.
            </p>
          </div>
        ) : sensors.length === 0 ? (
          <div className="py-16 flex flex-col items-center justify-center text-slate-500 text-center px-4 gap-2">
            <p className="text-xs sm:text-sm font-semibold text-slate-400">
              No Earth Pits Configured
            </p>
            <p className="text-xs text-slate-600 max-w-[220px]">
              No sensors or telemetry probes are registered for this system.
            </p>
          </div>
        ) : filteredSensors.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-xs">
            No earth pits match "{searchQuery}".
          </div>
        ) : (
          <ul className="space-y-2.5">
            {filteredSensors.map((sensor: any) => (
              <SiteSensorCard
                key={sensor.id || sensor.name}
                sensor={sensor}
                criticalThreshold={criticalThreshold}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Sensor count footer */}
      {selectedSystem && (
        <div
          className="px-4 py-2.5 flex items-center justify-between text-[11px] text-slate-400 flex-shrink-0"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            background: "rgba(0,0,0,0.15)",
          }}
        >
          <span>{sensors.length} total pits</span>
          {criticalThreshold != null && (
            <span className="text-amber-400/80 font-mono">
              Crit: ≥ {Number(criticalThreshold).toFixed(1)} Ω
            </span>
          )}
        </div>
      )}
    </aside>
  );
};

export default React.memo(SensorList);
